import { useMemo, useState } from 'react'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Avatar } from '@/shared/ui/Avatar'
import { StatusBadge } from '@/shared/ui/Badge'
import { DirhamAmount } from '@/shared/ui/Dirham'
import { EmptyState } from '@/shared/ui/EmptyState'
import { Skeleton } from '@/shared/ui/Skeleton'
import { cn } from '@/shared/utils'
import { GraduationCap, Car, UserCog, Layers, Sparkles, AlertTriangle, Wallet, Search, X, ClipboardList, CheckCircle2 } from 'lucide-react'
import { useAssistantPaymentListAll } from '@/api/hooks'
import type { AssistantPayment, AssistantPaymentPerson } from '@/api/services/assistantBookingService'

const assistantName = (p: AssistantPayment) => (p.assistant?.name ?? p.assistant_name ?? '').trim()

// Same derivation PaymentCard uses for its own badge, pulled out so the
// paid-count summary stays in sync with what each card actually shows.
const paymentStatusOf = (p: AssistantPayment) =>
  p.booking?.payment_status ?? (p.payment_status && p.payment_status !== '1' ? p.payment_status : null)
const isPaid = (p: AssistantPayment) => (paymentStatusOf(p) ?? '').toUpperCase() === 'PAID'

// Date + time (not just the date) for the created-at header.
function formatDateTime(date?: string | null) {
  if (!date) return '—'
  // The API sends "2026-09-20 22:42:53" — a space, not the "T" ISO parsing
  // needs — so `new Date(date)` as-is returns Invalid Date in Safari/Firefox.
  const d = new Date(String(date).replace(' ', 'T'))
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

// Top accent + left icon-badge colour per role/state — gives each card a
// quick, scannable identity instead of a wall of uniform gray text.
function cardAccent(hasBooking: boolean, bookingStatus?: string | null) {
  if (!hasBooking) return 'from-violet-400 to-purple-400'
  const s = (bookingStatus ?? '').toLowerCase()
  if (/cancel/.test(s)) return 'from-red-400 to-rose-400'
  if (/confirm|complet/.test(s)) return 'from-emerald-400 to-teal-400'
  return 'from-brand-400 to-amber-400'
}

/** One labelled person row — avatar with a small role-icon badge at the corner. */
function PersonRow({ icon, iconBg, label, person, fallbackId }: {
  icon: React.ReactNode
  iconBg: string
  label: string
  person?: Pick<AssistantPaymentPerson, 'name' | 'phone' | 'photo_url'> | null
  fallbackId?: number | null
}) {
  const name = person?.name?.trim() || (fallbackId != null ? `#${fallbackId}` : '—')
  return (
    <div className="flex items-center gap-3">
      <div className="relative flex-shrink-0">
        <Avatar name={name === '—' ? label : name} src={person?.photo_url} size="sm" />
        <span className={cn('absolute -bottom-1 -end-1 w-4 h-4 rounded-full flex items-center justify-center text-white ring-2 ring-white', iconBg)}>
          {icon}
        </span>
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
        <p className="text-sm font-semibold text-gray-800 truncate">
          {name}
          {person?.phone && <span className="ml-1.5 text-xs font-normal text-gray-400">{person.phone}</span>}
        </p>
      </div>
    </div>
  )
}

function PaymentCard({ p }: { p: AssistantPayment }) {
  const b = p.booking
  const amount = p.amount ?? b?.total_amount ?? null
  const paymentStatus = paymentStatusOf(p)
  // No booking_id means this isn't tied to a training booking at all — it's a
  // standalone service the assistant created directly. There's no student or
  // trainer to show in that case.
  const hasBooking = !!p.booking_id
  const reqId = `#AB${String(p.id).padStart(4, '0')}`

  return (
    <div className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden">
      <div className={cn('h-1 w-full bg-gradient-to-r', cardAccent(hasBooking, b?.booking_status))} />

      <div className="p-5 flex flex-col gap-4">
        {/* Header: request id + amount, booking status + timestamp */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1">{reqId}</p>
            <DirhamAmount value={amount} size={18} textClass="text-gray-900 text-xl" />
          </div>
          <div className="flex flex-col items-end gap-1.5">
            {b?.booking_status && <StatusBadge status={b.booking_status} />}
            {p.created_at && <span className="text-[11px] text-gray-400 whitespace-nowrap">{formatDateTime(p.created_at)}</span>}
          </div>
        </div>

        {/* People */}
        <div className="flex flex-col gap-3 pt-4 border-t border-gray-100">
          {hasBooking ? (
            <>
              <PersonRow icon={<GraduationCap size={9} />} iconBg="bg-blue-500" label="Student"
                person={b?.user} fallbackId={p.paid_on_behalf_student_id ?? b?.user_id} />
              <PersonRow icon={<Car size={9} />} iconBg="bg-emerald-500" label="Trainer"
                person={b?.driver} fallbackId={b?.driver_id} />
            </>
          ) : (
            <div className="flex items-center gap-2 rounded-lg bg-violet-50 border border-violet-100 px-3 py-2">
              <Sparkles size={13} className="text-violet-500 flex-shrink-0" />
              <span className="text-xs font-medium text-violet-700">Service created by the assistant</span>
            </div>
          )}
          <PersonRow icon={<UserCog size={9} />} iconBg="bg-violet-500" label="Assistant"
            person={p.assistant ?? (p.assistant_name ? { name: p.assistant_name } : null)} fallbackId={p.assistant_id} />
        </div>

        {/* Booking meta */}
        {hasBooking && (
          <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-gray-100">
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 rounded-lg px-2.5 py-1">
              <Layers size={11} className="text-gray-400" /> Booking <span className="font-semibold text-gray-700">#{p.booking_id}</span>
            </span>
            {b?.no_of_sessions != null && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-50 rounded-lg px-2.5 py-1">
                <span className="font-semibold text-gray-700">{b.no_of_sessions}</span> sessions
              </span>
            )}
            {paymentStatus && (
              <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                Payment <StatusBadge status={paymentStatus} />
              </span>
            )}
          </div>
        )}

        {/* Footer: method + remarks */}
        {(b?.booking_method || b?.payment_method || p.remarks) && (
          <div className="pt-4 border-t border-gray-100 flex flex-col gap-2">
            {(b?.booking_method || b?.payment_method) && (
              <div className="text-xs text-gray-500">
                {b?.booking_method && <span className="font-medium text-gray-600">{b.booking_method}</span>}
                {b?.payment_method && <span> · via {b.payment_method}</span>}
              </div>
            )}
            {p.remarks && <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">{p.remarks}</p>}
          </div>
        )}
      </div>
    </div>
  )
}

function CardSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="h-1 w-full bg-gray-100" />
      <div className="p-5 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2">
            <Skeleton className="h-2.5 w-16" />
            <Skeleton className="h-6 w-24" />
          </div>
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
        <div className="space-y-3 pt-4 border-t border-gray-100">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="w-8 h-8 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-2.5 w-1/3" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function AssistantBookingsPage() {
  const { rows, isLoading, isError } = useAssistantPaymentListAll()
  const [search, setSearch] = useState('')

  // The list endpoint has no server-side search param and this hook already
  // pulls every pending/approved row up front, so filtering by the assistant's
  // name happens client-side over what's already loaded.
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(p => assistantName(p).toLowerCase().includes(q))
  }, [rows, search])

  // Counts for whatever's currently on screen — the full list, or just the
  // matches for the assistant name being searched.
  const totalCount = filteredRows.length
  const paidCount = useMemo(() => filteredRows.filter(isPaid).length, [filteredRows])

  return (
    <div className="space-y-5">
      <PageHeader
        title="Assistant Bookings"
        subtitle="Payments made by assistants on behalf of students"
        breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Assistant Bookings' }]}
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search size={15} className="pointer-events-none absolute start-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by assistant name…"
            aria-label="Search by assistant name"
            className="w-full h-10 ps-10 pe-9 text-sm text-gray-700 placeholder-gray-400 bg-white border border-gray-200 rounded-xl transition-colors hover:border-gray-300 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-400/30"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              aria-label="Clear search"
              className="absolute end-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X size={13} />
            </button>
          )}
        </div>

        {!isLoading && !isError && (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
              <ClipboardList size={13} className="text-gray-400" />
              Total: <span className="text-gray-900">{totalCount}</span>
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
              <CheckCircle2 size={13} className="text-emerald-500" />
              Paid: <span className="text-emerald-900">{paidCount}</span>
            </span>
          </div>
        )}
      </div>

      {isError && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          <AlertTriangle size={16} className="flex-shrink-0" /> Failed to load assistant payments.
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <EmptyState
            icon={<Wallet size={22} />}
            message={search ? 'No matching assistant' : 'No payments found'}
            description={search ? `No payments found for an assistant named "${search}".` : 'There are no assistant payments yet.'}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filteredRows.map(p => <PaymentCard key={p.id} p={p} />)}
        </div>
      )}
    </div>
  )
}
